/* eslint-disable max-len */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { PhoneFilterOptions } from '@app/akita/api/products/models/phone-filter-options.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { SearchSortBy } from '@app/shared/models/api/listing/search/search-sort-by.enum';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { Observable, Subscription, tap } from 'rxjs';
import {
  generateFiltersID,
  parseProductSearchFilters,
  ProductSearchFilters,
  ProductSearchSortMode,
  PRODUCT_SORTING_BEST_SALES,
  PRODUCT_SORTING_CHEAPEST_FIRST,
  PRODUCT_SORTING_CHEAPEST_LAST,
} from '@app/akita/api/products/models/product-search.filters.model';
import { Params } from '@angular/router';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { ProductStorageTypeFilterComponent } from '../../../components/filters/product-storage-type-filter/product-storage-type-filter.component';
import { ProductScreenFilterComponent } from '../../../components/filters/product-screen-filter/product-screen-filter.component';
import { ProductRamFilterComponent } from '../../../components/filters/product-ram-filter/product-ram-filter.component';
import { ProductLaunchDateFilterComponent } from '../../../components/filters/product-launch-date-filter/product-launch-date-filter.component';
import { ProductGraphicsFilterComponent } from '../../../components/filters/product-graphics-filter/product-graphics-filter.component';
import { ProductCpuFilterComponent } from '../../../components/filters/product-cpu-filter/product-cpu-filter.component';
import { PhoneStorageFilterComponent } from '../../../components/filters/phone-storage-filter/phone-storage-filter.component';
import { PhoneColorFilterComponent } from '../../../components/filters/phone-color-filter/phone-color-filter.component';
import { PhoneModelFilterComponent } from '../../../components/filters/phone-model-filter/phone-model-filter.component';
import { ProductSeriesFilterComponent } from '../../../components/filters/product-series-filter/product-series-filter.component';
import { ProductBrandFilterComponent } from '../../../components/filters/product-brand-filter/product-brand-filter.component';
import { PhonePriceFilterComponent } from '../../../components/filters/phone-price-filter/phone-price-filter.component';
import { ArrowUp1IconComponent } from '../../../shared/components/icons/generated/arrow-up-1/arrow-up-1.component';
import { AddIconComponent } from '../../../shared/components/icons/generated/add/add.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { fromUrlSlug } from '@app/shared/utils/url.utils';

interface FilterCollapse {
  [filter: string]: boolean | null;
}

@Component({
  selector: 'app-featured-phone-filters-sidebar',
  templateUrl: './featured-phone-filters-sidebar.template.html',
  styleUrls: ['./featured-phone-filters-sidebar.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    AddIconComponent,
    ArrowUp1IconComponent,
    PhonePriceFilterComponent,
    ProductBrandFilterComponent,
    ProductSeriesFilterComponent,
    PhoneModelFilterComponent,
    PhoneColorFilterComponent,
    PhoneStorageFilterComponent,
    ProductCpuFilterComponent,
    ProductGraphicsFilterComponent,
    ProductLaunchDateFilterComponent,
    ProductRamFilterComponent,
    ProductScreenFilterComponent,
    ProductStorageTypeFilterComponent,
    AsyncPipe,
  ],
})
export class FeaturedPhoneFiltersSidebarComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() public phoneFilterOptions?: PhoneFilterOptions | null;
  @Input() public loading?: boolean | null;
  @Input() public allCollapsed?: boolean | null = true;

  @Output() public readonly filterPhoneModelsChanged: EventEmitter<Array<string>>;
  @Output() public readonly filterPhoneSeriesChanged: EventEmitter<Array<string>>;

  @Output() public readonly filterProductBrandChanged: EventEmitter<Array<string>>;
  @Output() public readonly filterPhoneColorsChanged: EventEmitter<Array<string>>;
  @Output() public readonly filterPhoneStoragesChanged: EventEmitter<Array<number>>;
  @Output() public readonly filterPhoneSortByChanged: EventEmitter<SearchSortBy>;
  @Output() public readonly filterPhonePriceRangeChanged: EventEmitter<{
    minPrice: number;
    maxPrice: number;
  }>;
  @Output() public readonly filterProductCpuChanged: EventEmitter<Array<string>>;
  @Output() public readonly filterProductGraphicsChanged: EventEmitter<Array<string>>;
  @Output() public readonly filterProductLaunchDateChanged: EventEmitter<Array<number>>;
  @Output() public readonly filterProductRamChanged: EventEmitter<Array<string>>;
  @Output() public readonly filterProductScreenSizeChanged: EventEmitter<Array<string>>;
  @Output() public readonly filterProductStorageTypeChanged: EventEmitter<Array<string>>;

  @Output() public readonly filterPhoneClearAll: EventEmitter<boolean>;

  public filterCollapsed: FilterCollapse;

  public readonly lessThan$: Observable<ScreenSizeInfoState>;

  public availableFilters: ProductSearchFilterOptions | null;

  public availableFilters$: Observable<ProductSearchFilterOptions>;
  public readonly fetchingProductOptions$: Observable<boolean>;
  public readonly productOptionsError$: Observable<ApiError | null>;

  public searchParams$: Observable<Params>;
  public searchOptionsPath: string | null;

  private readonly subscriptions: Subscription;

  constructor(
    private readonly zone: NgZone,
    private readonly translateService: TranslocoService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaProductsQuery: AkitaProductsQuery
  ) {
    this.subscriptions = new Subscription();
    this.searchOptionsPath = null;

    this.availableFilters = null;
    this.filterCollapsed = {};

    this.searchParams$ = this.akitaRouterQuery.selectQueryParams().pipe(
      tap((params: Params) => {
        const path = this.getFiltersPathFromQueryParams(params);
        this.searchOptionsPath = path;
      })
    );

    this.availableFilters$ = this.akitaProductsQuery.selectProductFilters(
      this.searchOptionsPath
    );
    this.fetchingProductOptions$ =
      this.akitaProductsQuery.selectIsFetchingProductFilters();
    this.productOptionsError$ =
      this.akitaProductsQuery.selectErrorFetchingProductFilters();

    // if we do not have availableProductFilters on the state
    if (
      !this.akitaProductsQuery.getProductFilters(this.searchOptionsPath)?.category?.length
    ) {
      if (this.akitaRouterQuery.isBrowser) {
        this.zone.runOutsideAngular(this.fetchProductOptionsInBG.bind(this));
      } else {
        this.fetchProductOptionsInBG();
      }
    }

    this.filterPhoneModelsChanged = new EventEmitter();
    this.filterPhoneSeriesChanged = new EventEmitter();
    this.filterProductBrandChanged = new EventEmitter();
    this.filterPhoneColorsChanged = new EventEmitter();
    this.filterPhoneStoragesChanged = new EventEmitter();
    this.filterPhoneSortByChanged = new EventEmitter();
    this.filterPhonePriceRangeChanged = new EventEmitter();
    this.filterProductCpuChanged = new EventEmitter();
    this.filterProductGraphicsChanged = new EventEmitter();
    this.filterProductLaunchDateChanged = new EventEmitter();
    this.filterProductRamChanged = new EventEmitter();
    this.filterProductScreenSizeChanged = new EventEmitter();
    this.filterProductStorageTypeChanged = new EventEmitter();

    this.filterPhoneClearAll = new EventEmitter();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
  }

  public ngOnInit(): void {
    this.refreshSearchOptions();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.collapseFilters();

      const path = this.getFiltersPathFromQueryParams(
        this.akitaRouterQuery.paramsAndQueryParams
      );
      this.searchOptionsPath = path;

      this.availableFilters$ = this.akitaProductsQuery.selectProductFilters(
        this.searchOptionsPath
      );

      // if we do not have availableProductFilters on the state
      if (
        !this.akitaProductsQuery.getProductFilters(this.searchOptionsPath)?.category
          ?.length
      ) {
        if (this.akitaRouterQuery.isBrowser) {
          this.zone.runOutsideAngular(this.fetchProductOptionsInBG.bind(this));
        } else {
          this.fetchProductOptionsInBG();
        }
      }
    }
  }

  private fetchProductOptionsInBG(): void {
    this.subscriptions.add(
      this.akitaProductsService.getAvailableSearchOptionsAsync(
        this.proccessFilters(this.akitaRouterQuery.paramsAndQueryParams)
      )
    );
  }

  private refreshSearchOptions(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.subscriptions.add(
          this.akitaProductsService.getAvailableSearchOptionsAsync(
            this.searchBaseOptionsPath
          )
        );
      });
    } else {
      this.subscriptions.add(
        this.akitaProductsService.getAvailableSearchOptionsAsync(
          this.searchBaseOptionsPath
        )
      );
    }
  }

  private collapseFilters(): void {
    this.filterCollapsed = {};
    this.filterCollapsed['price'] = false;
    this.filterCollapsed['model'] = false;
    this.filterCollapsed['brand'] = false;
    this.filterCollapsed['color'] = Boolean(this.allCollapsed);
    this.filterCollapsed['storage'] = Boolean(this.allCollapsed);
    this.filterCollapsed['cpu'] = Boolean(this.allCollapsed);
    this.filterCollapsed['graphics'] = Boolean(this.allCollapsed);
    this.filterCollapsed['launch_date'] = Boolean(this.allCollapsed);
    this.filterCollapsed['ram'] = Boolean(this.allCollapsed);
    this.filterCollapsed['screen_size'] = Boolean(this.allCollapsed);
    this.filterCollapsed['storage_type'] = Boolean(this.allCollapsed);
  }

  private get searchBaseOptionsPath(): PhoneFilterOptions {
    const options: PhoneFilterOptions = {
      // country: this.akitaRouterQuery.country,
      locale: this.translateService.getActiveLang(),
    };

    // if (this.phoneFilterOptions?.country) {
    //   options.country = this.phoneFilterOptions.country;
    // }
    if (this.phoneFilterOptions?.locale) {
      options.locale = this.phoneFilterOptions.locale;
    }
    return options;
  }

  public onPhoneModelChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterPhoneModelsChanged.emit($event);
    }
  }

  public onPhoneSeriesChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterPhoneSeriesChanged.emit($event);
    }
  }

  public onProductBrandChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterProductBrandChanged.emit($event);
    }
  }

  public onProductCpuChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterProductCpuChanged.emit($event);
    }
  }

  public onProductGraphicsChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterProductGraphicsChanged.emit($event);
    }
  }

  public onProductLaunchDateChanged($event?: Array<number> | null): void {
    if ($event) {
      this.filterProductLaunchDateChanged.emit($event);
    }
  }

  public onProductRamChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterProductRamChanged.emit($event);
    }
  }

  public onProductScreenSizeChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterProductScreenSizeChanged.emit($event);
    }
  }

  public onProductStorageTypeChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterProductStorageTypeChanged.emit($event);
    }
  }

  public onPhoneColorChanged($event?: Array<string> | null): void {
    if ($event) {
      this.filterPhoneColorsChanged.emit($event);
    }
  }

  public onPhoneStorageChanged($event?: Array<number> | null): void {
    if ($event) {
      this.filterPhoneStoragesChanged.emit($event);
    }
  }

  public onSortByChanged($event?: SearchSortBy | null): void {
    if ($event) {
      this.filterPhoneSortByChanged.emit($event);
    }
  }

  public onPriceRangeChange(
    $event?: {
      minPrice: number;
      maxPrice: number;
    } | null
  ): void {
    if ($event) {
      this.filterPhonePriceRangeChanged.emit($event);
    }
  }

  public getFiltersPathFromQueryParams(queryParams: Params): string {
    const parsedFilters = this.proccessFilters(queryParams);
    const path = generateFiltersID(parsedFilters);

    return path;
  }

  private proccessFilters(queryParams: Params): ProductSearchFilters {
    let modelsQuery = queryParams.models || null;

    let brandsQuery = queryParams.brands || null;
    let categoryQuery = queryParams.category || null;
    // filters on url fragments section and key
    switch (queryParams.section) {
      case 'category':
        categoryQuery = queryParams.key.toUpperCase();
        break;
      case 'brand':
        brandsQuery = queryParams.key.charAt(0).toUpperCase() + queryParams.key.slice(1);
        break;
      case 'model':
        modelsQuery = queryParams.key;
        break;
      default:
        break;
    }
    const seriesQuery = queryParams.series || null;
    const colorsQuery = queryParams.colors || null;
    const storagesQuery = queryParams.storages || null;
    const textQuery = queryParams.text || null;
    const cpuQuery = queryParams.cpu || null;
    const graphicsQuery = queryParams.graphics || null;
    const launchDateQuery = queryParams.launch_date || null;
    const ramQuery = queryParams.ram || null;
    const screenSizeQuery = queryParams.screen_size || null;
    const storageTypeQuery = queryParams.storage_type || null;

    const sortByQuery = queryParams.sort_by || null;
    const minPriceQuery = queryParams.min_price || null;
    const maxPriceQuery = queryParams.max_price || null;

    const phoneFilters = { ...this.phoneFilterOptions };

    let productSort: ProductSearchSortMode | null = null;
    let productStorages: Array<string> | null = null;
    let productDates: Array<string> | null = null;

    if (modelsQuery) {
      const models = fromUrlSlug(modelsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.models = models;
    } else {
      phoneFilters.models = [];
    }

    if (seriesQuery) {
      const series = decodeURI(seriesQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.series = series;
    } else {
      phoneFilters.series = [];
    }

    if (brandsQuery) {
      const brands = decodeURI(brandsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.brands = brands;
    } else {
      phoneFilters.brands = [];
    }

    if (categoryQuery) {
      const categories = decodeURI(categoryQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.category = categories;
    } else {
      phoneFilters.category = [];
    }

    if (colorsQuery) {
      const colors = decodeURI(colorsQuery).replace(/[+_]/g, ' ').split('|');
      phoneFilters.colors = colors;
    } else {
      phoneFilters.colors = [];
    }

    if (cpuQuery) {
      const cpus = decodeURI(cpuQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.cpu = cpus;
    } else {
      phoneFilters.cpu = [];
    }

    if (graphicsQuery) {
      const graphics = decodeURI(graphicsQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.graphics = graphics;
    } else {
      phoneFilters.graphics = [];
    }

    if (storagesQuery) {
      const storages = decodeURI(storagesQuery).replace(/\+/g, ' ').split('|');

      const parsedStorages: Array<number> = new Array(0);
      productStorages = new Array(0);
      for (const storage of storages) {
        parsedStorages.push(parseInt(storage, 10));
        productStorages.push(storage);
      }

      phoneFilters.storages = parsedStorages;
    } else {
      phoneFilters.storages = [];
    }

    if (launchDateQuery) {
      const launchDate = decodeURI(launchDateQuery).replace(/\+/g, ' ').split('|');

      const parsedDates: Array<number> = new Array(0);
      productDates = new Array(0);
      for (const date of launchDate) {
        parsedDates.push(parseInt(date, 10));
        productDates.push(date);
      }

      phoneFilters.launch_date = parsedDates;
    } else {
      phoneFilters.storages = [];
    }

    if (ramQuery) {
      const ram = decodeURI(ramQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.ram = ram;
    } else {
      phoneFilters.graphics = [];
    }

    if (screenSizeQuery) {
      const screenSize = decodeURI(screenSizeQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.screen_size = screenSize;
    } else {
      phoneFilters.graphics = [];
    }

    if (storageTypeQuery) {
      const storageType = decodeURI(storageTypeQuery).replace(/\+/g, ' ').split('|');
      phoneFilters.storage_type = storageType;
    } else {
      phoneFilters.graphics = [];
    }

    if (sortByQuery) {
      const sortByNormalized = `${sortByQuery || ''}`.toUpperCase();
      if (sortByNormalized === PRODUCT_SORTING_BEST_SALES) {
        phoneFilters.sortBy = SearchSortBy.NOT_SET;
        productSort = PRODUCT_SORTING_BEST_SALES;
      } else if (
        sortByNormalized === PRODUCT_SORTING_CHEAPEST_FIRST ||
        sortByNormalized === SearchSortBy.PRICE_ASC
      ) {
        phoneFilters.sortBy = SearchSortBy.PRICE_ASC;
        productSort = PRODUCT_SORTING_CHEAPEST_FIRST;
      } else if (
        sortByNormalized === PRODUCT_SORTING_CHEAPEST_LAST ||
        sortByNormalized === SearchSortBy.PRICE_DESC
      ) {
        phoneFilters.sortBy = SearchSortBy.PRICE_DESC;
        productSort = PRODUCT_SORTING_CHEAPEST_LAST;
      }
    } else {
      phoneFilters.sortBy = null;
    }

    if (minPriceQuery) {
      phoneFilters.minPrice = parseInt(`${minPriceQuery || 0}`, 10) || undefined;
    } else {
      phoneFilters.minPrice = undefined;
    }

    if (maxPriceQuery) {
      phoneFilters.maxPrice = parseInt(`${maxPriceQuery || 0}`, 10) || undefined;
    } else {
      phoneFilters.maxPrice = undefined;
    }

    if (textQuery) {
      phoneFilters.text = textQuery || undefined;
    } else {
      phoneFilters.text = undefined;
    }

    const searchOptions = parseProductSearchFilters();
    searchOptions.model = phoneFilters.models;
    searchOptions.series = phoneFilters.series;
    searchOptions.brand = phoneFilters.brands;
    searchOptions.category = phoneFilters.category;
    searchOptions.color = phoneFilters.colors;
    searchOptions.storage = productStorages;
    searchOptions.sorting = productSort;
    searchOptions.price_min = phoneFilters.minPrice;
    searchOptions.price_max = phoneFilters.maxPrice;
    searchOptions.text = phoneFilters.text;
    searchOptions.cpu = phoneFilters.cpu;
    searchOptions.graphics = phoneFilters.graphics;
    searchOptions.launch_date = phoneFilters.launch_date;
    searchOptions.ram = phoneFilters.ram;
    searchOptions.screen_size = phoneFilters.screen_size;
    searchOptions.storage_type = phoneFilters.storage_type;

    const language = `${this.translateService.getActiveLang() || ''}`.toLowerCase();
    if (language) {
      searchOptions.language = language;
    }

    const country = `${this.akitaRouterQuery.country || ''}`.toUpperCase();
    if (country) {
      searchOptions.country = country;
    }

    const validLocale = this.akitaProductsQuery.validateLocale(language, country);
    searchOptions.language = validLocale.language;
    searchOptions.country = validLocale.country;

    return searchOptions;
  }

  public clearFilters(): boolean {
    this.filterPhoneClearAll.emit(true);
    return false;
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
